/**
 * The three shared states `05-reporting-marking.md` §B9 requires and the mockups never
 * drew: a loading skeleton, an empty state and a fail-closed error banner.
 *
 * §B9 is explicit that a loading surface must **never** render a figure as though it were
 * a value ("never render a 0.0% stat box while loading"), which is why the skeleton draws
 * bars and no numbers.
 */

export function Skeleton({ rows = 6 }: { rows?: number }): React.JSX.Element {
  return (
    <div className="skeleton" role="status" aria-live="polite" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <i key={index} />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  detail,
}: {
  title: string;
  detail: string;
}): React.JSX.Element {
  return (
    <div className="emptystate">
      <b>{title}</b>
      {detail}
    </div>
  );
}

/**
 * `role` is `alert` unless the caller says otherwise, and a caller should say otherwise for one
 * reason only (RUN 82C): **the same failure is already announced by another banner on the page.**
 * The Command Desk draws two panels from one schedule-wide read, and two `alert`s for one refusal
 * interrupt a screen reader twice with the same sentence. The second banner is a `status` — still
 * on screen, still in the accessibility tree, and read when the reader reaches it.
 */
export function FailBanner({
  error,
  role = "alert",
}: {
  error: Error;
  role?: "alert" | "status";
}): React.JSX.Element {
  return (
    <div className="failbanner" role={role}>
      <span className="failbanner__label">Not Loaded</span>
      <span className="failbanner__msg">{error.message}</span>
    </div>
  );
}
